/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from "@solana/program-client-core";
import { findEventAuthorityPda } from "../pdas/index.js";
import { CP_AMM_PROGRAM_ADDRESS } from "../programs/index.js";

export const CLOSE_TOKEN_BADGE_DISCRIMINATOR: ReadonlyUint8Array =
  new Uint8Array([108, 146, 86, 110, 179, 254, 10, 104]);

export function getCloseTokenBadgeDiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    CLOSE_TOKEN_BADGE_DISCRIMINATOR,
  );
}

export type CloseTokenBadgeInstruction<
  TProgram extends string = typeof CP_AMM_PROGRAM_ADDRESS,
  TAccountTokenBadge extends string | AccountMeta<string> = string,
  TAccountAdmin extends string | AccountMeta<string> = string,
  TAccountRentReceiver extends string | AccountMeta<string> = string,
  TAccountEventAuthority extends string | AccountMeta<string> = string,
  TAccountProgram extends string | AccountMeta<string> =
    "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountTokenBadge extends string
        ? WritableAccount<TAccountTokenBadge>
        : TAccountTokenBadge,
      TAccountAdmin extends string
        ? WritableSignerAccount<TAccountAdmin> &
            AccountSignerMeta<TAccountAdmin>
        : TAccountAdmin,
      TAccountRentReceiver extends string
        ? WritableAccount<TAccountRentReceiver>
        : TAccountRentReceiver,
      TAccountEventAuthority extends string
        ? ReadonlyAccount<TAccountEventAuthority>
        : TAccountEventAuthority,
      TAccountProgram extends string
        ? ReadonlyAccount<TAccountProgram>
        : TAccountProgram,
      ...TRemainingAccounts,
    ]
  >;

export type CloseTokenBadgeInstructionData = {
  discriminator: ReadonlyUint8Array;
};

export type CloseTokenBadgeInstructionDataArgs = {};

export function getCloseTokenBadgeInstructionDataEncoder(): FixedSizeEncoder<CloseTokenBadgeInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({ ...value, discriminator: CLOSE_TOKEN_BADGE_DISCRIMINATOR }),
  );
}

export function getCloseTokenBadgeInstructionDataDecoder(): FixedSizeDecoder<CloseTokenBadgeInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getCloseTokenBadgeInstructionDataCodec(): FixedSizeCodec<
  CloseTokenBadgeInstructionDataArgs,
  CloseTokenBadgeInstructionData
> {
  return combineCodec(
    getCloseTokenBadgeInstructionDataEncoder(),
    getCloseTokenBadgeInstructionDataDecoder(),
  );
}

export type CloseTokenBadgeAsyncInput<
  TAccountTokenBadge extends string = string,
  TAccountAdmin extends string = string,
  TAccountRentReceiver extends string = string,
  TAccountEventAuthority extends string = string,
  TAccountProgram extends string = string,
> = {
  tokenBadge: Address<TAccountTokenBadge>;
  admin: TransactionSigner<TAccountAdmin>;
  rentReceiver: Address<TAccountRentReceiver>;
  eventAuthority?: Address<TAccountEventAuthority>;
  program?: Address<TAccountProgram>;
};

export async function getCloseTokenBadgeInstructionAsync<
  TAccountTokenBadge extends string,
  TAccountAdmin extends string,
  TAccountRentReceiver extends string,
  TAccountEventAuthority extends string,
  TAccountProgram extends string,
  TProgramAddress extends Address = typeof CP_AMM_PROGRAM_ADDRESS,
>(
  input: CloseTokenBadgeAsyncInput<
    TAccountTokenBadge,
    TAccountAdmin,
    TAccountRentReceiver,
    TAccountEventAuthority,
    TAccountProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  CloseTokenBadgeInstruction<
    TProgramAddress,
    TAccountTokenBadge,
    TAccountAdmin,
    TAccountRentReceiver,
    TAccountEventAuthority,
    TAccountProgram
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? CP_AMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    tokenBadge: { value: input.tokenBadge ?? null, isWritable: true },
    admin: { value: input.admin ?? null, isWritable: true },
    rentReceiver: { value: input.rentReceiver ?? null, isWritable: true },
    eventAuthority: { value: input.eventAuthority ?? null, isWritable: false },
    program: { value: input.program ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Resolve default values.
  if (!accounts.eventAuthority.value) {
    accounts.eventAuthority.value = await findEventAuthorityPda();
  }
  if (!accounts.program.value) {
    accounts.program.value =
      "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG" as Address<"cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("tokenBadge", accounts.tokenBadge),
      getAccountMeta("admin", accounts.admin),
      getAccountMeta("rentReceiver", accounts.rentReceiver),
      getAccountMeta("eventAuthority", accounts.eventAuthority),
      getAccountMeta("program", accounts.program),
    ],
    data: getCloseTokenBadgeInstructionDataEncoder().encode({}),
    programAddress,
  } as CloseTokenBadgeInstruction<
    TProgramAddress,
    TAccountTokenBadge,
    TAccountAdmin,
    TAccountRentReceiver,
    TAccountEventAuthority,
    TAccountProgram
  >);
}

export type CloseTokenBadgeInput<
  TAccountTokenBadge extends string = string,
  TAccountAdmin extends string = string,
  TAccountRentReceiver extends string = string,
  TAccountEventAuthority extends string = string,
  TAccountProgram extends string = string,
> = {
  tokenBadge: Address<TAccountTokenBadge>;
  admin: TransactionSigner<TAccountAdmin>;
  rentReceiver: Address<TAccountRentReceiver>;
  eventAuthority: Address<TAccountEventAuthority>;
  program?: Address<TAccountProgram>;
};

export function getCloseTokenBadgeInstruction<
  TAccountTokenBadge extends string,
  TAccountAdmin extends string,
  TAccountRentReceiver extends string,
  TAccountEventAuthority extends string,
  TAccountProgram extends string,
  TProgramAddress extends Address = typeof CP_AMM_PROGRAM_ADDRESS,
>(
  input: CloseTokenBadgeInput<
    TAccountTokenBadge,
    TAccountAdmin,
    TAccountRentReceiver,
    TAccountEventAuthority,
    TAccountProgram
  >,
  config?: { programAddress?: TProgramAddress },
): CloseTokenBadgeInstruction<
  TProgramAddress,
  TAccountTokenBadge,
  TAccountAdmin,
  TAccountRentReceiver,
  TAccountEventAuthority,
  TAccountProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? CP_AMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    tokenBadge: { value: input.tokenBadge ?? null, isWritable: true },
    admin: { value: input.admin ?? null, isWritable: true },
    rentReceiver: { value: input.rentReceiver ?? null, isWritable: true },
    eventAuthority: { value: input.eventAuthority ?? null, isWritable: false },
    program: { value: input.program ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Resolve default values.
  if (!accounts.program.value) {
    accounts.program.value =
      "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG" as Address<"cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("tokenBadge", accounts.tokenBadge),
      getAccountMeta("admin", accounts.admin),
      getAccountMeta("rentReceiver", accounts.rentReceiver),
      getAccountMeta("eventAuthority", accounts.eventAuthority),
      getAccountMeta("program", accounts.program),
    ],
    data: getCloseTokenBadgeInstructionDataEncoder().encode({}),
    programAddress,
  } as CloseTokenBadgeInstruction<
    TProgramAddress,
    TAccountTokenBadge,
    TAccountAdmin,
    TAccountRentReceiver,
    TAccountEventAuthority,
    TAccountProgram
  >);
}

export type ParsedCloseTokenBadgeInstruction<
  TProgram extends string = typeof CP_AMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    tokenBadge: TAccountMetas[0];
    admin: TAccountMetas[1];
    rentReceiver: TAccountMetas[2];
    eventAuthority: TAccountMetas[3];
    program: TAccountMetas[4];
  };
  data: CloseTokenBadgeInstructionData;
};

export function parseCloseTokenBadgeInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedCloseTokenBadgeInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 5) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 5,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      tokenBadge: getNextAccount(),
      admin: getNextAccount(),
      rentReceiver: getNextAccount(),
      eventAuthority: getNextAccount(),
      program: getNextAccount(),
    },
    data: getCloseTokenBadgeInstructionDataDecoder().decode(instruction.data),
  };
}
